import React, { useState, useEffect, useContext } from 'react';
import "./styles/routesModule.scss";
import RoutesList from './RoutesList';
import RouteForm from './RouteForm';
import RouteDetails from './RouteDetails';
import NfcTagsManager from './utils/NfcTagsManager';
import RouteRecordsFilter from './RouteRecordsFilter';
import { AuthContext } from "../../context/AuthContext";
import { 
  fetchClientesPorAdministrativo, 
  fetchClientesPorInspector, 
  fetchTiendasPorCampoCliente 
} from "../admin/firestoreService";

const RoutesModule = () => {
  const [activeView, setActiveView] = useState('list'); // 'list', 'create', 'edit', 'details', 'nfc-tags', 'records'
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [filteredServicios, setFilteredServicios] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState('');
  const [selectedServicio, setSelectedServicio] = useState('');

  const { currentUser, currentService, adminData } = useContext(AuthContext);

  useEffect(() => {
    const loadClientes = async () => {
      if (currentUser?.email && currentService?.id && adminData?.id) {
        try {
          let clientesList = [];
          
          // Usar las funciones de firestoreService según el tipo de usuario
          if (adminData.tipoUsuario?.includes('administrativo')) {
            clientesList = await fetchClientesPorAdministrativo(adminData.id, currentService);
          } else if (adminData.tipoUsuario?.includes('inspector')) {
            clientesList = await fetchClientesPorInspector(adminData.id, currentService);
          } else if (adminData.tipoUsuario?.includes('administrador')) {
            // Si es administrador, cargar todos los clientes
            const { fetchClientesYTiendas } = await import("../admin/firestoreService");
            const [clientesData] = await fetchClientesYTiendas(currentService);
            clientesList = clientesData;
          }
          
          setClientes(clientesList);
        } catch (error) {
          console.error('Error loading clientes:', error);
          setClientes([]);
        }
      }
    };
    loadClientes();
  }, [currentUser, currentService, adminData]);

  // Cargar servicios (tiendas) basados en los clientes permitidos
  useEffect(() => {
    const loadServicios = async () => {
      if (currentService?.id && clientes.length > 0) {
        try {
          const clientesIds = clientes.map(c => c.id);
          const serviciosList = await fetchTiendasPorCampoCliente(clientesIds, currentService);
          setServicios(serviciosList);
        } catch (error) {
          console.error('Error loading servicios:', error);
          setServicios([]);
        }
      } else {
        setServicios([]);
      }
    };
    loadServicios();
  }, [currentService, clientes]);

  // Filtrar servicios por cliente seleccionado
  useEffect(() => {
    if (selectedCliente) {
      const filtered = servicios.filter(servicio =>
        servicio.cliente === selectedCliente ||
        servicio.clienteId === selectedCliente ||
        servicio.nombreCliente === selectedCliente
      );
      setFilteredServicios(filtered);
    } else {
      setFilteredServicios(servicios);
    }
  }, [selectedCliente, servicios]);

  const handleCreateRoute = () => {
    setSelectedRoute(null);
    setActiveView('create');
  };

  const handleEditRoute = (route) => {
    setSelectedRoute(route);
    setActiveView('edit');
  };

  const handleViewDetails = (route) => {
    setSelectedRoute(route);
    setActiveView('details');
  };

  const handleViewRecords = () => {
    setActiveView('records');
  };

  const handleBack = () => {
    setActiveView('list');
    setSelectedRoute(null);
  };

  const handleClienteChange = (clienteId) => {
    setSelectedCliente(clienteId);

    // Resetear servicio seleccionado al cambiar el cliente
    setSelectedServicio('');
  };

  const handleServicioChange = (servicioId) => {
    setSelectedServicio(servicioId);
  };

  const renderContent = () => {
    switch (activeView) {
      case 'list':
        return (
          <div className="routes-module-content">
            <div className="module-tabs">
              <button
                className="tab-button active"
                onClick={() => setActiveView('list')}
              >
                Rutas
              </button>
              <button
                className="tab-button"
                onClick={() => setActiveView('nfc-tags')}
              >
                Etiquetas NFC
              </button>
              <button
                className="tab-button"
                onClick={handleViewRecords}
              >
                Registros
              </button>
            </div>

            <div className="filters-container">
              <div className="filter-group">
                <label>Cliente:</label>
                <select
                  value={selectedCliente}
                  onChange={(e) => handleClienteChange(e.target.value)}
                >
                  <option value="">Todos los clientes</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Servicio:</label>
                <select
                  value={selectedServicio}
                  onChange={(e) => handleServicioChange(e.target.value)}
                >
                  <option value="">Todos los servicios</option>
                  {filteredServicios.map(servicio => (
                    <option key={servicio.id} value={servicio.id}>
                      {servicio.numTienda || servicio.nombre || servicio.descripcion || servicio.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <RoutesList
              onCreateRoute={handleCreateRoute}
              onEditRoute={handleEditRoute}
              onViewDetails={handleViewDetails}
              clienteId={selectedCliente}
              servicioId={selectedServicio}
            />
          </div>
        );
      case 'create':
        return (
          <RouteForm
            onBack={handleBack}
            clienteId={selectedCliente}
            servicioId={selectedServicio}
          />
        );
      case 'edit':
        return <RouteForm route={selectedRoute} onBack={handleBack} />;
      case 'details':
        return <RouteDetails route={selectedRoute} onBack={handleBack} onEdit={() => setActiveView('edit')} />;
      case 'nfc-tags':
        return (
          <div className="routes-module-content">
            <div className="module-tabs">
              <button
                className="tab-button"
                onClick={() => setActiveView('list')}
              >
                Rutas
              </button>
              <button
                className="tab-button active"
                onClick={() => setActiveView('nfc-tags')}
              >
                Etiquetas NFC
              </button>
              <button
                className="tab-button"
                onClick={handleViewRecords}
              >
                Registros
              </button>
            </div>
            <NfcTagsManager onBack={handleBack} />
          </div>
        );
      case 'records':
        return (
          <div className="routes-module-content">
            <div className="module-tabs">
              <button
                className="tab-button"
                onClick={() => setActiveView('list')}
              >
                Rutas
              </button>
              <button
                className="tab-button"
                onClick={() => setActiveView('nfc-tags')}
              >
                Etiquetas NFC
              </button>
              <button
                className="tab-button active"
                onClick={handleViewRecords}
              >
                Registros
              </button>
            </div>
            <RouteRecordsFilter
              onBack={handleBack}
              clientes={clientes}
              servicios={servicios}
            />
          </div>
        );
      default:
        return <RoutesList onCreateRoute={handleCreateRoute} />;
    }
  };

  return (
    <div className="routes-module">
      <div className="module-header">
        <h1>Control de Rondas</h1>
      </div>
      <div className="module-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default RoutesModule;